// Julkalenderspel landing page, milestone 1: phone notice, loading screen, start, journey with
// music, skip, stream offer, and the hand-over to the arrival loop. The computer screen itself
// comes in milestone 2.
//
// Phases: loading -> ready -> journey -> arrived, with error reachable from loading/ready/journey.
// "Hoppa över" and Escape lead to arrival from every phase before it. Every asynchronous step checks
// that the phase (and the playback attempt) it belongs to is still current before it acts.

import "./style.css";

// The JavaScript page is shown only when this script runs; without it, the <noscript> list is all.
document.documentElement.classList.add("js");
import { NOTICE, support } from "./device";
import { choose, loadMeta, sizeOf, type Choice, type MediaMeta } from "./media";
import { download, isAbort, type Download } from "./loader";
import { Stage } from "./stage";
import { Music, musicUrl } from "./audio";
import { Scene } from "./scene";

type Phase = "loading" | "ready" | "journey" | "arrived" | "error";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const app = $<HTMLElement>("app");
const stageEl = $<HTMLDivElement>("stage");
const journey = $<HTMLVideoElement>("journey");
const loopVid = $<HTMLVideoElement>("loop");
const still = $<HTMLImageElement>("still");
const status = $<HTMLParagraphElement>("status");
const startBtn = $<HTMLButtonElement>("start");
const skipBtn = $<HTMLButtonElement>("skip");
const soundBtn = $<HTMLButtonElement>("sound");
const streamBtn = $<HTMLButtonElement>("stream");
const notice = $<HTMLElement>("notice");

/** After this long without the whole film, offer to stream it instead. */
const STREAM_OFFER_MS = 8000;

let phase: Phase = "loading";
const stage = new Stage(stageEl);
let blocked = false;
let soundOn = true;
/** Incremented whenever a journey playback attempt becomes obsolete (skip, arrival, error). */
let attempt = 0;
let pick: Choice | null = null;
let journeyDl: Download | null = null;
let journeyUrl: string | null = null;
let streaming = false;
let loopReady: Promise<string | null> = Promise.resolve(null);
/** Arrival state: the hand-over is done once a picture of the room is presented. */
let looping = false;
let handedOver = false;
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

const music = new Music(() => (phase === "journey" ? journey.currentTime : 0));
const scene = new Scene({ stage, stageEl, appEl: app, loopVid, reducedMotion });

function setPhase(p: Phase): void {
  phase = p;
  app.dataset.phase = p;
  const before = p === "loading" || p === "ready" || p === "journey" || p === "error";
  skipBtn.hidden = !before;
  startBtn.hidden = p !== "ready";
  soundBtn.hidden = !(p === "journey" || p === "arrived");
  if (p !== "loading") streamBtn.hidden = true;
}

/** The phase as of now (not as narrowed by TypeScript before an await). */
function currentPhase(): Phase {
  return phase;
}

function say(text: string, blink = false): void {
  status.textContent = text;
  status.classList.toggle("blink", blink);
}

// ---------- phone / tablet notice ----------

/** The notice covers everything while the window does not qualify; the page keeps its place. */
function watchSupport(onFirstOk: () => void): void {
  let started = false;
  const check = () => {
    const s = support();
    const nowBlocked = s !== "ok";
    if (nowBlocked) {
      $<HTMLHeadingElement>("notice-title").textContent = NOTICE[s].title;
      $<HTMLParagraphElement>("notice-body").textContent = NOTICE[s].body;
    }
    if (nowBlocked !== blocked) {
      blocked = nowBlocked;
      notice.hidden = !blocked;
      app.classList.toggle("blocked", blocked);
      scene.setSuspended(blocked);
      if (blocked) {
        if (phase === "journey") journey.pause();
        loopVid.pause();
        music.suspend();
      } else {
        if (phase === "journey" && !journey.ended) void journey.play().catch(() => undefined);
        if (phase === "arrived") {
          // once the scene has its close-up, it owns the picture: the wide loop stays paused
          if (!scene.ownsPicture) {
            if (looping) void loopVid.play().catch(() => undefined);
            else void upgradeToLoop(); // the loop may have become ready while the notice was up
          }
          music.resume();
        }
      }
    }
    if (!blocked && !started) {
      started = true;
      onFirstOk();
    }
  };
  window.addEventListener("resize", check);
  window.addEventListener("orientationchange", check);
  check();
}

// ---------- loading ----------

async function boot(): Promise<void> {
  setPhase("loading");
  say("LADDAR …");
  let meta: MediaMeta;
  try {
    meta = await loadMeta();
    pick = await choose(meta);
  } catch (e) {
    return fail(e);
  }
  if (phase !== "loading") return; // skipped while the manifest loaded
  app.dataset.media = pick.label;
  const p = pick;

  const mUrl = musicUrl();
  const total = sizeOf(meta, p.journey) + sizeOf(meta, p.loop) + sizeOf(meta, mUrl);
  const dls: Download[] = [];
  const progress = () => {
    if (phase !== "loading" || streaming) return;
    const got = dls.reduce((n, d) => n + d.received(), 0);
    const pct = total ? Math.min(99, Math.floor((got / total) * 100)) : 0;
    say(`LADDAR RESAN ${pct} %`);
  };

  journeyDl = download(p.journey, progress);
  const loopDl = download(p.loop, progress);
  const musicDl = download(mUrl, progress);
  dls.push(journeyDl, loopDl, musicDl);
  // The close-up loop for after the glide: small, not counted in the loading progress.
  const closeDl = download(p.close, () => undefined);
  scene.setCloseSource(closeDl.result.then((r) => (r.ok ? r.url : null)));

  // The loop: needed at arrival only. Arrival waits for this promise and shows a still meanwhile.
  loopReady = loopDl.result.then((r) => {
    if (!r.ok) {
      console.warn("[julkalenderspel] loop unavailable", r.error);
      return null;
    }
    loopVid.src = r.url;
    loopVid.load();
    return r.url;
  });

  // The music: optional, never holds up the film. If it arrives late it joins at the right place.
  void musicDl.result.then(async (r) => {
    if (!r.ok) return console.warn("[julkalenderspel] music unavailable", r.error);
    try {
      const buf = await (await fetch(r.url)).arrayBuffer();
      URL.revokeObjectURL(r.url);
      await music.prepare(buf);
    } catch (e) {
      console.warn("[julkalenderspel] music could not be decoded", e);
    }
  });

  // Slow connection: offer to stream the film instead of waiting for all of it.
  const offer = window.setTimeout(() => {
    if (phase === "loading") streamBtn.hidden = false;
  }, STREAM_OFFER_MS);

  const r = await journeyDl.result;
  window.clearTimeout(offer);
  if (!r.ok) {
    if (!streaming && !isAbort(r.error) && phase === "loading") fail(r.error);
    return;
  }
  if (phase !== "loading" || streaming) {
    URL.revokeObjectURL(r.url); // finished after a skip or after streaming began: not needed
    return;
  }
  journeyUrl = r.url;
  journey.src = r.url;
  journey.load();
  ready();
}

streamBtn.addEventListener("click", () => {
  if (phase !== "loading" || !pick || blocked) return;
  streaming = true;
  journeyDl?.abort(); // do not compete with the stream for the same bytes
  journey.preload = "auto";
  journey.src = pick.journey;
  ready();
  start(); // this click is the visitor's start gesture
});

function fail(e: unknown): void {
  if (phase === "arrived" || phase === "error") return;
  console.error("[julkalenderspel]", e);
  attempt++;
  journey.pause();
  music.suspend(); // explicitly: the queued pause event will see phase "error" and do nothing
  setPhase("error");
  say("FILMEN GICK INTE ATT SPELA. HOPPA ÖVER FÖR ATT GÅ IN.");
}

// ---------- start ----------

function ready(): void {
  if (phase !== "loading") return;
  setPhase("ready");
  say("");
  startBtn.focus({ preventScroll: true });
}

/** Any ordinary key starts; Escape skips; Tab and modifiers keep their normal meaning. */
window.addEventListener("keydown", (ev) => {
  if (blocked) return;
  if (ev.key === "Escape") {
    if (phase === "loading" || phase === "ready" || phase === "journey" || phase === "error") {
      ev.preventDefault();
      void arrive("skip");
    }
    return;
  }
  if (phase !== "ready" || ev.repeat || ev.metaKey || ev.ctrlKey || ev.altKey) return;
  if (ev.key === "Tab" || ev.key === "Shift") return;
  if (ev.target instanceof HTMLButtonElement && ev.target !== startBtn && (ev.key === "Enter" || ev.key === " ")) return;
  ev.preventDefault();
  start();
});

startBtn.addEventListener("click", () => start());
// A click anywhere on the picture also starts (click, not pointerdown: touch grants sound on release).
stageEl.addEventListener("click", () => {
  if (phase === "ready") start();
});

/** Runs inside the visitor's gesture: everything up to play() is synchronous. */
function start(): void {
  if (phase !== "ready" || blocked) return;
  setPhase("journey");
  say("");
  const my = ++attempt;
  music.unlock(); // permission only; the music clock starts on the film's "playing" event
  journey.muted = !soundOn;
  const played = journey.play();
  music.journey();
  updateSound();
  played.catch((e: unknown) => {
    if (my !== attempt || phase !== "journey") return; // skipped or superseded meanwhile
    if (e instanceof DOMException && e.name === "NotAllowedError" && !journey.muted) {
      // The browser refused sound: play without it; the button turns it on.
      soundOn = false;
      updateSound();
      journey.play().catch((e2: unknown) => {
        if (my === attempt && phase === "journey" && !isAbort(e2)) fail(e2);
      });
    } else if (!isAbort(e)) {
      fail(e);
    }
  });
}

function updateSound(): void {
  journey.muted = !soundOn;
  music.setMuted(!soundOn);
  soundBtn.textContent = soundOn ? "Ljud av" : "Ljud på";
  soundBtn.setAttribute("aria-pressed", String(soundOn));
}

soundBtn.addEventListener("click", () => {
  soundOn = !soundOn;
  if (soundOn) music.unlock();
  updateSound();
});

// The music follows the film: frozen whenever the film is not actually playing.
journey.addEventListener("playing", () => {
  if (phase !== "journey" || blocked) return;
  if (streaming) say("");
  music.resume();
});
journey.addEventListener("pause", () => {
  // Not at the natural end: the film's own pause event comes just before "ended", and suspending
  // the AudioContext there costs a ~2 s audio restart in Firefox (measured by Codex, Task 025),
  // heard as the music cutting out at the arrival.
  if (phase === "journey" && !journey.ended) music.suspend();
});
journey.addEventListener("waiting", () => {
  if (phase !== "journey") return;
  music.suspend();
  if (streaming) say("LADDAR …");
});
journey.addEventListener("error", () => {
  if (phase === "journey" || phase === "ready") fail(journey.error);
});

// ---------- arrival ----------

/** Wait at most `ms` for a promise; resolves `fallback` on timeout. */
function within<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([p, new Promise<T>((r) => window.setTimeout(() => r(fallback), ms))]);
}

/** The arrival still, decoded (true) or unavailable (false). Started at page load. */
const stillReady: Promise<boolean> = still.decode().then(
  () => true,
  () => false,
);

async function arrive(how: "ended" | "skip"): Promise<void> {
  if (phase === "arrived") return;
  attempt++; // any pending journey play() or retry is now obsolete
  journey.pause();
  if (journeyDl && !journeyDl.settled()) journeyDl.abort();
  setPhase("arrived");
  say("");
  updateSound();
  music.unlock(); // a skip is a click or key press: the music may start here
  music.menu(how === "ended" ? 3 : 2);

  // Present the room as a still once it has decoded. Until then the picture already on screen
  // stays up: the film's last frame after a natural end, the first frame or the film after a skip.
  const ok = await within(stillReady, 3000, false);
  if (currentPhase() !== "arrived") return;
  if (ok) presentStill();
  // A still that decodes after the deadline is still put up when it arrives.
  else void stillReady.then((late) => late && presentStill());
  handedOver = true;
  window.dispatchEvent(new CustomEvent("jks:arrived", { detail: { how, still: ok } }));
  void upgradeToLoop();
}

function presentStill(): void {
  if (currentPhase() !== "arrived" || app.classList.contains("show-still")) return;
  app.classList.add("show-still");
  window.setTimeout(releaseJourney, 700); // after the still's fade-in has covered the film
}

/** Start the arrival loop over the still when it is ready; independent of the hand-over. */
async function upgradeToLoop(): Promise<void> {
  const url = await loopReady;
  if (currentPhase() !== "arrived" || !handedOver || looping || !url || reducedMotion.matches || blocked) return;
  if (scene.ownsPicture) return; // the close-up has already replaced the wide picture
  try {
    loopVid.currentTime = 0;
    await loopVid.play();
  } catch (e) {
    console.warn("[julkalenderspel] loop did not start", e);
    return;
  }
  if (currentPhase() !== "arrived" || looping) return;
  looping = true;
  app.classList.add("looping");
  releaseJourney(); // the loop now covers everything below it
  window.dispatchEvent(new CustomEvent("jks:looping"));
}

/** The journey is over for this visit: free its decoder and its blob storage (once). */
function releaseJourney(): void {
  if (!journey.hasAttribute("src") && !journeyUrl) return;
  journey.removeAttribute("src");
  journey.load();
  if (journeyUrl) URL.revokeObjectURL(journeyUrl);
  journeyUrl = null;
}

loopVid.addEventListener("error", () => {
  if (phase !== "arrived") return;
  looping = false;
  app.classList.remove("looping");
});

journey.addEventListener("ended", () => {
  if (phase === "journey") void arrive("ended");
});
skipBtn.addEventListener("click", () => void arrive("skip"));
window.addEventListener("jks:leaving", () => music.fadeOut(0.8));
window.addEventListener("jks:depart", () => music.close());
// Coming back with the browser's back button from a game: the page may be restored from the cache.
window.addEventListener("pageshow", (ev) => {
  if (ev.persisted && phase === "arrived") music.menu(1.5);
});
// A context made without a gesture (after Back) starts at the visitor's next key press or click.
for (const t of ["keydown", "pointerdown"] as const) {
  window.addEventListener(t, () => {
    if (phase === "arrived") music.unlock();
  });
}

// ---------- go ----------

watchSupport(() => void boot());

// For the headless checks: read-only view of the state.
(window as unknown as { __jks: object }).__jks = {
  phase: () => phase,
  media: () => app.dataset.media,
  stage: () => stage,
  scene: () => scene.info(),
  loseContext: () => scene.loseContext(),
  musicRace: () => music.raceTest(),
  release: () => (window as unknown as { __release?: () => void }).__release?.(),
  music: () => ({ level: music.level, ready: music.ready, muted: music.isMuted, sources: music.sources, state: music.state }),
};
